import config from 'Cloud/config';
import classNames from 'clsx';
import sha1 from 'js-sha1';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { IS_WEBVIEW } from 'reactApp/appHelpers/configHelpers';
import { sendSuperAppXray, superAppSubmetrics } from 'reactApp/appHelpers/sendSuperAppAnalytics';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getCurrentPublicItem, isOwnPublic } from 'reactApp/modules/public/public.selectors';
import type { PublicFile } from 'reactApp/modules/public/public.types';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { Button } from 'reactApp/ui/Button/Button';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import { SocialButtons } from 'reactApp/ui/SocialButtons/SocialButtons';
import { LinkIcon, MailIcon } from 'reactApp/ui/VKUIIcons';
import { copy } from 'reactApp/utils/copyToClipboard';
import { sendDwh } from 'reactApp/utils/ga';
import { ECategoryGa } from 'reactApp/utils/paymentGa';
import { EQueryParams } from 'server/helpers/getRequestParams';

import styles from './ViewerShareButton.css';

const IS_ONPREMISE = config.get('ON_PREMISE');

const isMobile = EnvironmentSelectors.isMobile();

const isNativeShareSupported = isMobile && 'share' in navigator && typeof navigator.share === 'function';

const fromDeeplink = getQueryParams()[EQueryParams.fromDeeplink];
const isSuperAppWebView = Boolean(IS_WEBVIEW) && fromDeeplink;

const mapDispatchToProps = (dispatch): IDispatchToProps => ({
    showNotify: (options) => dispatch(showSnackbarAction(options)),
});

interface IDispatchToProps {
    showNotify: (data) => void;
}

interface IMapState {
    isOwn: boolean;
    item: PublicFile;
}

const mapStateToProps = (state): IMapState => {
    const item = getCurrentPublicItem(state) as PublicFile;
    const isOwn = isOwnPublic(state);

    return { isOwn, item };
};

interface IProps extends IDispatchToProps {
    onSocial?: (data) => void;
    socialList?: string[];
    placement: string;
    basic?: boolean;
    sendGaRadar?: (data) => void;
}

interface Props extends IProps, IMapState {}

class ViewerShareButtonComponent extends PureComponent<Props> {
    copyLink = () => {
        const { showNotify } = this.props;

        copy(window.location.href)
            .then(({ hasBeenCopied }) => {
                if (hasBeenCopied) {
                    showNotify({
                        text: 'Ссылка успешно скопирована',
                        type: SnackbarTypes.success,
                        id: 'copyLink',
                        closable: true,
                    });
                }
            })
            .catch(() => {
                showNotify({
                    text: 'Не удалось скопировать ссылку',
                    type: SnackbarTypes.failure,
                    id: 'copyLink',
                    closable: true,
                });
            });
    };

    handleOnSocial = (id) => {
        const { sendGaRadar, onSocial } = this.props;

        if (sendGaRadar) {
            sendGaRadar(`${id}-click`);
        }

        onSocial?.(id);
    };

    handleOnSharingClick = (id) => {
        switch (id) {
            case 'send':
                return this.handleOnSocial('email');
            case 'copy':
                return this.copyLink();
        }
    };

    handleOnButtonClick = () => {
        const { sendGaRadar } = this.props;

        if (isSuperAppWebView) {
            const isOwn = this.props.isOwn;
            const item = this.props.item;
            sendSuperAppXray(superAppSubmetrics.sharePublic, {
                id_public: isOwn && item?.home ? sha1(item.home) : item?.id,
                id_file: item?.id,
                type_public: 'file',
                count_objects: 0,
                extension: item?.ext,
                owner: isOwn,
                have_faces: false,
                count_faces: 0,
            });
        }

        if (sendGaRadar) {
            sendGaRadar('share-click');
        }
    };

    onNativeShare = () => {
        const { sendGaRadar } = this.props;

        if (isSuperAppWebView) {
            const isOwn = this.props.isOwn;
            const item = this.props.item;
            sendSuperAppXray(superAppSubmetrics.sharePublic, {
                id_public: isOwn && item?.home ? sha1(item.home) : item?.id,
                id_file: item?.id,
                type_public: 'file',
                count_objects: 0,
                extension: item?.ext,
                owner: isOwn,
                have_faces: false,
                count_faces: 0,
            });
        }

        if (isNativeShareSupported) {
            /* tempexp_17777-start */
            const item = this.props.item;
            sendDwh({
                eventCategory: ECategoryGa.public,
                action: 'share-click-in-header_touch',
                dwhData: {
                    extension: item?.ext,
                },
            });
            /* tempexp_17777-end */

            navigator
                .share({
                    url: window.location.href,
                })
                .catch(() => {
                    if (sendGaRadar) {
                        sendGaRadar('share-error');
                    }
                });
        }
    };

    renderSharingDropdown = () => {
        const list = [
            {
                id: 'copy',
                text: 'Скопировать ссылку',
                icon: <LinkIcon height={20} width={20} />,
            },
        ];

        if (!IS_ONPREMISE) {
            list.push({
                id: 'send',
                text: 'Отправить по почте',
                icon: <MailIcon />,
            });
        }

        return (
            <>
                <DropdownMenu list={list} onClick={this.handleOnSharingClick} />
                {this.props.socialList && (
                    <div className={styles.socials}>
                        <SocialButtons size="big" dark socials={this.props.socialList} onClick={this.handleOnSocial} />
                    </div>
                )}
            </>
        );
    };

    render() {
        const { basic, placement } = this.props;
        return (
            <div
                className={classNames({
                    [styles.root_basic]: basic,
                })}
            >
                {isNativeShareSupported ? (
                    <Button onClick={this.onNativeShare} size="big" basic={basic} color={basic ? undefined : 'black'} theme="octavius">
                        <LinkIcon className={styles.icon} height={20} width={20} fill="currentColor" />
                        <span className={styles.text}>Поделиться</span>
                    </Button>
                ) : (
                    <Dropdown
                        as={Button}
                        size="big"
                        theme="octavius"
                        basic={basic}
                        color={basic ? null : 'black'}
                        content={this.renderSharingDropdown()}
                        placement={placement}
                    >
                        <div onClick={this.handleOnButtonClick} className={styles.link}>
                            <LinkIcon className={styles.icon} height={20} width={20} fill="currentColor" />
                            <span className={styles.text}>Поделиться</span>
                        </div>
                    </Dropdown>
                )}
            </div>
        );
    }
}

export const ViewerShareButton = connect(mapStateToProps, mapDispatchToProps)(ViewerShareButtonComponent);
